/**
 * CMMC gaps + requirement drill-down model — V102-8-3 / V102-8-4 (ADR-0056;
 * ux-adversary run cmmc-self-assessment, round 7 `gaps.web.html` and
 * `requirements.web.html`; PR B critic panel). Pure: no React, no fetch.
 *
 * Inputs are the server's: the objective-readiness tree (requirement →
 * objective → asset, already rolled up with the AND rule), the score (points,
 * POA&M eligibility, collection-only set, the Level 1 → Level 2 links), the
 * tests (per-objective criteria), the determinations (the records behind each
 * cell, and the proposals awaiting review), the stale list, the POA&M, the
 * posture connectors and the findings. The client never re-decides a status;
 * it names it, orders it and attaches the next action.
 *
 * Rules carried from the design review and the critic panel:
 *   H12   the gaps hero is one lever with its number split from the sentence;
 *         a clock (conditional status ending) or a blocked state leads instead.
 *   H13   every requirement expands to its objective × asset matrix; every cell
 *         opens its record; the strategy panel follows the selected objective.
 *   H8/H9 a Met on evidence past its window is "Met · stale" with its age; a
 *         cadence shows last done and next due (with the year when not this year).
 *   H2-8  a freshness suffix only past about half the window.
 *   J5-H1 one org-wide judgment is ONE cell (one record), not one per asset.
 *   D2-09 N/A shows its rationale.
 *   F1    every Not met row has a real action by gap type: Add to POA&M,
 *         Update the SSP, Re-check, or new evidence; findings only when there are some.
 *   F2    the connector verb follows connector state: Collect now when connected,
 *         Connect <source> only when not.
 *   L1    a Level 1 profile lists the 15 FAR requirements (no points, no POA&M);
 *         each opens its linked Level 2 rows' objectives.
 *   No vendor-technical text outside Verify (test key, version, hash).
 */
import type { AffirmationGate, AffirmationRecord, AssessmentDetermination, CmmcScoreResult, ObjectiveReadiness, PoamItem, StaleMet } from '@aigovops/core';
import type { ProgramTestRow } from './api';
import { daysUntil, fmtWithYear, level1Lapse, plural, reqRef, rowAnchor, type Tone } from './cmmc-model';

type CellStatus = 'met' | 'not_met' | 'not_applicable' | 'no_evidence' | 'undetermined';
type ReqStatus = 'met' | 'not_met' | 'no_evidence' | 'undetermined';

const DAY = 86_400_000;
const reqs = (n: number) => plural(n, 'requirement', 'requirements');
const pts = (n: number) => `${n} pt${n === 1 ? '' : 's'}`;
const daysWord = (n: number) => plural(n, 'day', 'days');
const dash = (ref: string) => ref.replace(/[^A-Za-z0-9]+/g, '-');

/** The one status vocabulary (glyph + words + tone). Three-tone system (PR #57 R1 aesthetic High #2):
 * red is reserved for Not met only; amber + "?" signals No evidence; muted + a dashed dot signals
 * Not assessed — so the three states don't flatten into "all red" in the status column. */
export const STATUS: Record<CellStatus, { glyph: string; label: string; tone: Tone | 'ok' | 'muted' }> = {
  met: { glyph: '✓', label: 'Met', tone: 'ok' },
  not_met: { glyph: '✕', label: 'Not met', tone: 'red' },
  no_evidence: { glyph: '?', label: 'No evidence', tone: 'amber' },
  undetermined: { glyph: '◌', label: 'Not assessed', tone: 'muted' },
  not_applicable: { glyph: '–', label: 'N/A', tone: 'neutral' },
};

/** Gap types as plain sentences (ADR-0056 vocabulary). */
export const GAP_WORDS: Record<string, string> = {
  not_documented: 'Not written down',
  not_implemented: 'Not in place',
  not_operating: 'In place, but not working',
  not_effective: 'Working, but not enough',
  no_evidence: 'No evidence',
};

const STRATEGY_WORDS: Record<string, string> = {
  configuration_state: 'Settings checked at a point in time',
  behavior_over_period: 'Behaviour checked over a period',
  process_sample: 'A sample of records checked',
  cross_source_reconciliation: 'Two sources reconciled',
  adversarial_test: 'Tested directly',
  document_and_interview: 'A document examined and people interviewed',
  inherited_from_provider: 'Inherited from a provider',
};
const METHOD_WORDS: Record<string, string> = { examine: 'Examine', interview: 'Interview', test: 'Test' };
const AUTOMATION_WORDS: Record<string, string> = { high: 'Automated', partial: 'Partly automated', manual: 'By a person' };
const AUTOMATED = new Set(['configuration_state', 'behavior_over_period', 'cross_source_reconciliation', 'adversarial_test']);

/**
 * Evidence-source categories (the test library's vocabulary) a read-only
 * posture connector can collect, by connector kind. A source with no entry
 * (documents, interviews, tickets, HR, endpoint tools not yet built) is
 * uploaded or requested instead.
 */
export const SOURCE_KINDS: Record<string, readonly string[]> = {
  'Identity provider / directory': ['okta_posture', 'entra_posture'],
  'Cloud control plane': ['aws', 'aws_oidc', 'aws_role', 'gcp'],
  'Vulnerability scanner': ['tenable'],
  'Audit logs / SIEM': ['datadog'],
};
const KIND_LABEL: Record<string, string> = {
  okta_posture: 'Okta', entra_posture: 'Microsoft Entra ID', aws: 'AWS', aws_oidc: 'AWS', aws_role: 'AWS', gcp: 'Google Cloud', tenable: 'Tenable', datadog: 'Datadog',
};
/** A connector kind's vendor (to pick the identity provider the workspace already uses). */
const KIND_VENDOR: Record<string, string> = { okta_posture: 'okta', entra_posture: 'entra', aws: 'aws', aws_oidc: 'aws', aws_role: 'aws', gcp: 'gcp', tenable: 'tenable', datadog: 'datadog' };

export function windowWords(w: { kind: string; days?: number; size?: number } | undefined | null): string {
  if (!w) return 'Point in time';
  if (w.kind === 'window') return w.days ? `Evidence no older than ${w.days} days` : 'Over the period';
  if (w.kind === 'sample') return w.size ? `A sample of ${w.size}` : 'A sample';
  if (w.kind === 'current_document') return 'The current approved version';
  return 'Point in time';
}

export interface Freshness { days: number; windowDays: number | null; stale: boolean; suffix: string | null }

/** H2-8 / H8: "Met" while fresh; "Met · 4 mo" past half the window; "Met · 7 mo · stale" past it. */
export function freshness(judgedAt: string | null, windowDays: number | null, todayDay: string): Freshness | null {
  if (!judgedAt) return null;
  const days = Math.max(0, Math.round((Date.parse(`${todayDay}T00:00:00Z`) - Date.parse(`${judgedAt.slice(0, 10)}T00:00:00Z`)) / DAY));
  if (!windowDays) return { days, windowDays: null, stale: false, suffix: null };
  const stale = days > windowDays;
  const age = days >= 60 ? `${Math.round(days / 30)} mo` : `${days}d`;
  return { days, windowDays, stale, suffix: stale ? `${age} · stale` : days > windowDays / 2 ? age : null };
}

const CADENCE = (w: number) => (w >= 360 ? 'Yearly' : w >= 85 && w <= 95 ? 'Quarterly' : w >= 28 && w <= 31 ? 'Monthly' : `Every ${w} days`);

/** H9: "Yearly · last Sep 1, 2025 · due Sep 1, 2026 · 35 days overdue" (years shown when either date is not this year). */
export function staleLine(judgedAt: string, windowDays: number, todayDay: string): { text: string; lead: string; tail: string; overdue: boolean } {
  const due = new Date(Date.parse(`${judgedAt.slice(0, 10)}T00:00:00Z`) + windowDays * DAY).toISOString();
  const n = daysUntil(due, todayDay);
  const tail = n < 0 ? `${daysWord(-n)} overdue` : n === 0 ? 'due today' : `in ${daysWord(n)}`;
  const year = judgedAt.slice(0, 4) !== todayDay.slice(0, 4) || due.slice(0, 4) !== todayDay.slice(0, 4);
  const fmt = (iso: string) => (year ? fmtWithYear(iso, '0000-01-01') : fmtWithYear(iso, todayDay));
  const lead = `${CADENCE(windowDays)} · last ${fmt(judgedAt)} · due ${fmt(due)}`;
  return { text: `${lead} · ${tail}`, lead, tail, overdue: n < 0 };
}

// ---- inputs --------------------------------------------------------------------------------

export interface ConnectorState { connector_id: string; kind: string; status: string }
export interface FindingState { finding_id: string; control_id: string | null; status: string }

export interface AssessInput {
  readiness: ObjectiveReadiness;
  score?: CmmcScoreResult | null;
  tests?: readonly ProgramTestRow[];
  /** Reviewed records and proposals (GET …/determinations). */
  determinations?: readonly AssessmentDetermination[];
  stale?: readonly StaleMet[];
  poam?: readonly PoamItem[];
  gate?: AffirmationGate | null;
  affirmations?: readonly AffirmationRecord[];
  /** Posture connectors (read: any member). */
  connectors?: readonly ConnectorState[];
  /** Workspace findings (read: any member); mapped requirement → objectives → tests → controls. */
  findings?: readonly FindingState[];
  todayDay: string;
}

// ---- drill-down -------------------------------------------------------------------------------

export interface Proposal { id: string; by: string; at: string; result: string; gap: string | null; reason: string | null }

export interface DrillCell {
  key: string;
  /** Null for an org-wide objective: ONE cell, one record (J5-H1). */
  assetId: string | null;
  assetName: string;
  status: CellStatus;
  label: string;
  determinationId: string | null;
  gap: string | null;
  fresh: Freshness | null;
  /** F7: a result recorded and waiting for review on this cell. */
  proposal: Proposal | null;
}

export interface Strategy {
  checked: string;
  metWhen: string;
  how: string;
  sources: string[];
  window: string;
  scope: string;
  method: string;
  typicalGap: string | null;
  primary: string | null;
  /** Behind Verify only. */
  verify: Array<{ testKey: string; version: number | null; basis: string | null; hash: string | null; role: string }>;
}

export interface DrillObjective {
  id: string;
  ref: string;
  letter: string;
  /** "[a]" (or "3.10.3[a]" on a Level 1 row that links several Level 2 rows). */
  tag: string;
  statement: string;
  /** "[a] Authorized users are identified". */
  name: string;
  status: CellStatus;
  orgWide: boolean;
  cells: DrillCell[];
  strategy: Strategy | null;
  /** Why it is not assessed, in words. */
  reason: string | null;
  /** The Level 2 requirement this objective belongs to. */
  requirementId: string;
}

export interface DrillRequirement {
  id: string;
  ref: string;
  title: string;
  status: ReqStatus;
  label: string;
  tone: Tone | 'ok' | 'muted';
  points: number | null;
  /** Can't go on a POA&M (never / ineligible; every Level 1 requirement). */
  mustMet: boolean;
  allNA: boolean;
  naRationale: string | null;
  stale: boolean;
  anchor: string;
  objectives: DrillObjective[];
  family: string;
  /** The Level 2 refs behind it (itself on Level 2; the linked rows on Level 1). */
  l2Refs: string[];
}

export interface DrillModel {
  level: '1' | '2';
  requirements: DrillRequirement[];
  assets: Array<{ id: string; name: string; category: string }>;
  counts: { total: number; met: number; notMet: number; notAssessed: number; stale: number; ptsLost: number; score: number | null; threshold: number | null };
  /** G23: conditional is blocked (below the line, or a must-Met Not met) — the headline takes the same red as Gaps. */
  blocked: boolean;
  families: Array<{ id: string; count: number }>;
}

const familyOf = (ref: string, level: '1' | '2') => (level === '1' ? ref.split('.')[0]! : ref.split('.').slice(0, 2).join('.'));

function strategyFor(objectiveId: string, statement: string, tests: readonly ProgramTestRow[]): Strategy | null {
  const mapped = tests.filter((t) => t.objectives.some((o) => o.objective_id === objectiveId));
  if (!mapped.length) return null;
  const primary = mapped.find((t) => t.objectives.some((o) => o.objective_id === objectiveId && o.role === 'primary')) ?? mapped[0]!;
  const s = primary.objectives.find((o) => o.objective_id === objectiveId)?.strategy ?? null;
  const methods = primary.version?.methods ?? [];
  const primaryStrategy = s?.primary_strategy ?? primary.version?.primary_strategy ?? null;
  return {
    // F6: what is checked is the objective's own statement; the criteria say when it is Met.
    checked: statement || primary.title,
    metWhen: s?.met_criteria ?? primary.title,
    how: STRATEGY_WORDS[primaryStrategy ?? ''] ?? 'Checked against its criteria',
    sources: s?.evidence_sources ?? [],
    window: windowWords(s?.window),
    scope: s?.scope === 'org_wide' ? 'Once for the organization' : 'Each assessed asset',
    // H3-4: the method follows the evidence the test reads.
    method: [methods.map((m) => METHOD_WORDS[m] ?? m).join(' · '), AUTOMATION_WORDS[s?.automation ?? '']].filter(Boolean).join(' · ') || 'Examine',
    typicalGap: s?.typical_gap ?? null,
    primary: primaryStrategy,
    verify: mapped.map((t) => ({
      testKey: t.test_key, version: t.version?.version ?? null, basis: t.version?.basis ?? null, hash: t.version?.version_hash ?? null,
      role: t.objectives.find((o) => o.objective_id === objectiveId)?.role ?? 'primary',
    })),
  };
}

const windowDaysOf = (objectiveId: string, tests: readonly ProgramTestRow[]): number | null => {
  for (const t of tests) {
    const s = t.objectives.find((o) => o.objective_id === objectiveId)?.strategy;
    if (s?.window?.kind === 'window' && s.window.days) return s.window.days;
  }
  return null;
};

const RANK: Record<CellStatus, number> = { not_met: 4, undetermined: 3, no_evidence: 2, met: 1, not_applicable: 0 };
const worst = (s: readonly CellStatus[]): CellStatus => (s.length ? s.reduce((w, c) => (RANK[c] > RANK[w] ? c : w), 'not_applicable' as CellStatus) : 'undetermined');

/** List order (F13): must-Met Not met, other Not met, Not assessed (points lost first), stale Met, Met, N/A. */
function sortRank(r: DrillRequirement): number {
  if (r.status === 'not_met') return r.mustMet ? 0 : 1;
  if (r.status !== 'met') return 2;
  if (r.allNA) return 5;
  return r.stale ? 3 : 4;
}

export function buildDrill(input: AssessInput): DrillModel {
  const { readiness, tests = [], determinations = [], score, todayDay } = input;
  const level: '1' | '2' = score?.level === '1' ? '1' : '2';
  const dets = new Map(determinations.map((d) => [d.determination_id, d] as const));
  const proposals = determinations.filter((d) => d.state === 'proposed');
  const staleObj = new Set((input.stale ?? []).map((s) => s.objective_id));
  const unmet = new Map((score?.level2?.unmet ?? []).map((u) => [u.requirement_id, u] as const));
  const assets = new Map<string, { id: string; name: string; category: string }>();

  const l2 = readiness.requirements.map<DrillRequirement>((r) => {
    const objectives = r.objectives.map<DrillObjective>((o) => {
      const win = windowDaysOf(o.objective_id, tests);
      const cellOf = (assetId: string | null, assetName: string, status: CellStatus, determinationId: string | null, gapType: string | null): DrillCell => {
        const d = determinationId ? dets.get(determinationId) : undefined;
        const fresh = status === 'met' ? freshness(d?.proposed_at ?? null, win, todayDay) : null;
        const stale = status === 'met' && (staleObj.has(o.objective_id) || !!fresh?.stale);
        const p = status === 'undetermined' ? proposals.find((x) => x.objective_id === o.objective_id && (x.asset_id === assetId || x.asset_id === null)) : undefined;
        const proposal: Proposal | null = p ? { id: p.determination_id, by: p.proposed_by, at: p.proposed_at, result: p.result, gap: p.gap_type ? GAP_WORDS[p.gap_type] ?? null : null, reason: p.rationale ?? null } : null;
        const label = proposal ? 'Awaiting review'
          : status === 'met' ? (stale ? `Met · ${fresh?.suffix?.replace(' · stale', '') ?? 'old'} · stale` : fresh?.suffix ? `Met · ${fresh.suffix}` : 'Met') : STATUS[status].label;
        return {
          key: `${o.objective_id}|${assetId ?? 'org'}`, assetId, assetName, status, label, determinationId, gap: gapType ? GAP_WORDS[gapType] ?? null : null,
          fresh: stale && fresh ? { ...fresh, stale: true } : fresh, proposal,
        };
      };
      const cells = o.assets
        ? o.assets.map((a) => { assets.set(a.asset_id, { id: a.asset_id, name: a.asset_name, category: a.category }); return cellOf(a.asset_id, a.asset_name, a.status as CellStatus, a.determination_id, a.gap_type); })
        : [cellOf(null, 'Org-wide', o.status as CellStatus, o.determination_id, o.gap_type)];
      const letter = (/\[([^\]]+)\]$/.exec(o.objective_id)?.[1]) ?? o.ref;
      const statement = (o as { statement?: string }).statement ?? '';
      return {
        id: o.objective_id, ref: o.ref, letter, tag: `[${letter}]`, statement, name: statement ? `[${letter}] ${statement}` : `[${letter}]`,
        status: o.status as CellStatus, orgWide: o.scope === 'org_wide', cells, strategy: strategyFor(o.objective_id, statement, tests),
        reason: o.reason === 'no_in_scope_assets' ? 'No assessed asset in scope' : o.reason === 'no_reviewed_determination' ? 'Waiting for a reviewed result' : null,
        requirementId: r.requirement_id,
      };
    });
    const status = r.status as ReqStatus;
    const u = unmet.get(r.requirement_id);
    const stale = status === 'met' && objectives.some((o) => o.cells.some((c) => c.fresh?.stale));
    const naRec = r.all_not_applicable ? objectives.flatMap((o) => o.cells).map((c) => (c.determinationId ? dets.get(c.determinationId) : undefined)).find((d) => d?.rationale) : undefined;
    const label = r.all_not_applicable ? 'N/A · counts as Met' : stale ? 'Met · stale' : STATUS[status].label;
    return {
      id: r.requirement_id, ref: r.ref, title: r.title, status, label, tone: r.all_not_applicable ? 'neutral' : stale ? 'neutral' : STATUS[status].tone,
      points: u?.points ?? null, mustMet: !!u && (u.poam_eligibility === 'never' || u.poam_eligibility === 'ineligible'),
      allNA: r.all_not_applicable, naRationale: naRec?.rationale ?? null, stale, anchor: `req-${dash(r.ref)}`, objectives, family: familyOf(r.ref, '2'), l2Refs: [r.ref],
    };
  });

  let requirements = l2;
  if (level === '1' && score) {
    // L1: the 15 FAR requirements; each opens its linked Level 2 rows' objectives. No points, no POA&M.
    const byId = new Map(l2.map((r) => [r.id, r] as const));
    requirements = score.level1.requirements.map<DrillRequirement>((q) => {
      const rows = q.l2.map((id) => byId.get(id)).filter((x): x is DrillRequirement => !!x);
      const ref = reqRef(q.requirement_id);
      const several = rows.length > 1;
      const objectives = rows.flatMap((x) => x.objectives.map((o) => (several ? { ...o, tag: o.ref, name: o.statement ? `${o.ref} ${o.statement}` : o.ref } : o)));
      const rolled = worst(rows.map((x) => x.status as CellStatus));
      // The score decides Met; an unmet one says why from its rows (Not met, or not assessed yet).
      const status: ReqStatus = q.status === 'met' ? 'met' : rolled === 'met' || rolled === 'not_applicable' ? 'not_met' : (rolled as ReqStatus);
      const stale = status === 'met' && rows.some((x) => x.stale);
      const allNA = rows.length > 0 && rows.every((x) => x.allNA);
      return {
        id: q.requirement_id, ref, title: rows[0]?.title ?? ref, status, label: allNA ? 'N/A · counts as Met' : stale ? 'Met · stale' : STATUS[status].label,
        tone: allNA || stale ? 'neutral' : STATUS[status].tone, points: null, mustMet: status !== 'met',
        allNA, naRationale: rows.find((x) => x.naRationale)?.naRationale ?? null, stale, anchor: `req-${dash(ref)}`, objectives,
        family: familyOf(ref, '1'), l2Refs: rows.map((x) => x.ref),
      };
    });
  }

  requirements = [...requirements].sort((a, b) => sortRank(a) - sortRank(b) || (b.points ?? 0) - (a.points ?? 0) || a.ref.localeCompare(b.ref, 'en', { numeric: true }));
  const fam = new Map<string, number>();
  for (const r of [...requirements].sort((a, b) => a.ref.localeCompare(b.ref, 'en', { numeric: true }))) fam.set(r.family, (fam.get(r.family) ?? 0) + 1);
  return {
    level, requirements, assets: [...assets.values()],
    counts: {
      total: requirements.length,
      // F15: "met" counts Met, stale Met and N/A — the chip and the headline say the same number.
      met: requirements.filter((r) => r.status === 'met').length,
      notMet: requirements.filter((r) => r.status === 'not_met').length,
      notAssessed: requirements.filter((r) => r.status === 'no_evidence' || r.status === 'undetermined').length,
      stale: requirements.filter((r) => r.stale).length,
      ptsLost: level === '2' ? (score?.level2?.unmet ?? []).reduce((n, x) => n + x.points, 0) : 0,
      score: level === '2' ? score?.level2?.score ?? null : null,
      threshold: level === '2' ? score?.level2?.conditional_threshold ?? null : null,
    },
    blocked: level === '2' && !!score?.level2 && (score.level2.score < score.level2.conditional_threshold || requirements.some((r) => r.status === 'not_met' && r.mustMet)),
    families: [...fam].map(([id, count]) => ({ id, count })),
  };
}

/** F13 headline: "63 pts · 22 of 42 met (3 stale) · 47 pts lost"; Level 1 "11 of 15 met". */
// ---- V102-8-5: one cell's record history --------------------------------------------------------

/** A determination row as GET …/determinations serves it (cited refs named; the review hand-off). */
export type RecordRow = AssessmentDetermination & {
  evidence?: ReadonlyArray<{ kind: string; id: string; title: string | null; href: string | null; status?: string | null; observed_at?: string | null }>;
  review_request?: { reviewer_user_id: string; requested_by: string; at: string; official: boolean } | null;
  sent_back?: { by: string; at: string; reason: string } | null;
};

export interface CellHistory {
  /** The proposal awaiting review (a first result, a revision, or a replacement of `reviewed`). */
  open: RecordRow | null;
  /** The counted result. */
  reviewed: RecordRow | null;
  /** The latest proposal sent back, when it is the cell's newest event (the recorder's turn to revise). */
  returned: RecordRow | null;
  /** The send-back before the open proposal (its reviewer sees the earlier note). */
  earlierReturn: RecordRow | null;
}

const stamp = (d: RecordRow) => d.sent_back?.at ?? d.superseded_at ?? d.reviewed_at ?? d.proposed_at;

/** Self determinations of ONE cell (objective × asset, null = org-wide), from the period's records. */
export function cellHistory(records: Iterable<RecordRow>, objectiveId: string, assetId: string | null): CellHistory {
  const rows = [...records].filter((d) => d.objective_id === objectiveId && (d.asset_id ?? null) === assetId && d.kind === 'self');
  const open = rows.find((d) => d.state === 'proposed') ?? null;
  const reviewed = rows.find((d) => d.state === 'reviewed') ?? null;
  const returns = rows.filter((d) => d.state === 'superseded' && !d.superseded_by_id && d.sent_back).sort((a, b) => (stamp(a) < stamp(b) ? 1 : -1));
  const latest = returns[0] ?? null;
  const newerThan = (x: RecordRow, iso: string | null | undefined) => !iso || stamp(x) > iso;
  const returned = !open && latest && newerThan(latest, reviewed?.reviewed_at) ? latest : null;
  const earlierReturn = open ? returns.find((x) => stamp(x) <= open.proposed_at) ?? null : null;
  return { open, reviewed, returned, earlierReturn };
}

export function drillHeadline(m: DrillModel): string {
  const c = m.counts;
  const met = `${c.met} of ${c.total} met${c.stale ? ` (${c.stale} stale)` : ''}`;
  if (m.level === '1' || c.score === null) return met;
  return `${pts(c.score)}${c.threshold !== null ? ` · needs ${c.threshold}` : ''} · ${met} · ${pts(c.ptsLost)} lost`;
}

/** PR #57 R1 aesthetic High #1: the headline rendered as 3–4 stat tiles instead of a red run-on sentence.
 * Only the single failing stat (points lost, points short) carries the danger tone. */
export interface HeroStat { value: string; caption: string; tone?: 'danger'; }

export function drillHeadlineStats(m: DrillModel): HeroStat[] {
  const c = m.counts;
  if (m.level === '1' || c.score === null) {
    return [{ value: `${c.met}/${c.total}`, caption: `requirements met${c.stale ? ` · ${c.stale} stale` : ''}` }];
  }
  const stats: HeroStat[] = [{ value: String(c.score), caption: 'points earned' }];
  if (c.threshold !== null) {
    const short = c.score < c.threshold;
    stats.push({ value: String(c.threshold), caption: short ? `needed · ${c.threshold - c.score} short` : 'needed to pass', tone: short ? 'danger' : undefined });
  }
  stats.push({ value: `${c.met}/${c.total}`, caption: `requirements met${c.stale ? ` · ${c.stale} stale` : ''}` });
  if (c.ptsLost > 0) stats.push({ value: String(c.ptsLost), caption: 'points lost', tone: 'danger' });
  return stats;
}

/** Decompose a Gaps hero into stat tiles. The big number (if any) is the lead tile; each ' · '
 * segment of text / sub becomes its own tile with a parsed leading number when present. Danger
 * tone is reserved for the single failing segment ("blocked", "pts short", "pts lost", "not met")
 * — or the clock segment ("9 days", "ends", "lapsed", "overdue", "closed") when the whole hero
 * carries a red / amber tone (i.e. the clock IS the failure). */
const BAD_RX = /pts (short|lost)|blocked|not met/i;
const CLOCK_RX = /\bdays?\b|lapsed|lapses|ends|overdue|closed/i;
const LEAD_NUM = /^(\d+(?:\.\d+)?)\s*(.*)$/;

export function gapsHeroStats(h: GapsHero): HeroStat[] {
  const toneOf = (s: string): 'danger' | undefined => {
    if (BAD_RX.test(s)) return 'danger';
    if ((h.tone === 'red' || h.tone === 'amber') && CLOCK_RX.test(s)) return 'danger';
    return undefined;
  };
  const stats: HeroStat[] = [];
  const segs = [h.text, h.sub]
    .filter((x): x is string => !!x && x.trim() !== '')
    .flatMap((s) => s.split(' · ').map((x) => x.trim()).filter(Boolean));
  if (h.big) {
    const first = segs.shift() ?? '';
    stats.push({ value: `${h.big}${h.unit ?? ''}`, caption: first, tone: toneOf(first) });
  }
  for (const s of segs) {
    const m = LEAD_NUM.exec(s);
    const tone = toneOf(s);
    if (m) stats.push({ value: m[1]!, caption: m[2] || s, tone });
    else stats.push({ value: '', caption: s, tone });
  }
  return stats;
}

/** Landing on #req-…: the first failing, then missing, then waiting cell (F19). */
export function firstOpenCell(r: DrillRequirement): { obj: string; cell: string } | null {
  for (const want of ['not_met', 'no_evidence', 'undetermined'] as const) {
    for (const o of r.objectives) {
      const c = o.cells.find((x) => x.status === want);
      if (c) return { obj: o.id, cell: c.key };
    }
  }
  const o = r.objectives[0];
  return o && o.cells[0] ? { obj: o.id, cell: o.cells[0].key } : null;
}

// ---- gaps -----------------------------------------------------------------------------------

export type GapFix =
  | { kind: 'connect'; label: string; connectorKind: string; source: string }
  | { kind: 'collect'; label: string; connectorId: string; source: string }
  | { kind: 'upload'; label: string }
  | { kind: 'request'; label: string }
  | { kind: 'review'; label: string; determinationId: string }
  | { kind: 'poam'; label: string; determinationId: string }
  | { kind: 'poam_open'; label: string; due: string; days: number; tone: Tone }
  | { kind: 'ssp'; label: string }
  | { kind: 'recheck'; label: string; connectorId: string; source: string }
  | { kind: 'nightly'; label: string };

/** F18 / NEW-4: one link in the chain "<asset> → [a] <objective>", pointing at its cell. */
export interface ChainLink { where: string; tag: string; name: string; cellKey: string }

export interface GapRow {
  id: string;
  ref: string;
  title: string;
  anchor: string;
  /** The requirement's row in the drill-down. */
  reqAnchor: string;
  points: number | null;
  mustMet: boolean;
  /** What's missing, in words (only the sources of objectives with no evidence). */
  missing: string;
  fix: GapFix;
  /** A POA&M row's second action: what closes it (by gap type). */
  then?: GapFix | null;
  /** Objectives an upload can be bound to (no-evidence ones first). */
  uploadFor: Array<{ id: string; ref: string }>;
  /** Open findings mapped to this requirement (F1: the link shows only when n > 0). */
  findings: string[];
  l2Refs: string[];
  /** Not met: the gap in plain words, why, and the chain of failing cells (F18). */
  gap?: string | null;
  why?: string | null;
  chain?: ChainLink[];
  onPoam?: boolean;
  /** Stale: "Yearly · last … · due …" + " · N days overdue" (only the tail is red). */
  staleText?: string | null;
  staleLead?: string | null;
  staleTail?: string | null;
  overdue?: boolean;
}

/**
 * G6 / N4: one lever = one named source group — "Connect Datadog · 3.3.1",
 * "Collect now · Tenable, Okta", "Upload evidence · 7 requirements".
 */
export interface Lever {
  kind: 'connect' | 'collect' | 'upload' | 'request';
  label: string;
  count: number;
  pts: number;
  rows: GapRow[];
  first: GapRow;
  /** connect: the connector kind; collect: the connected sources to run. */
  connectorKind?: string;
  run?: Array<{ connectorId: string; source: string }>;
}

export type HeroPrimary =
  | { kind: 'lever'; lever: Lever }
  /** `fix` is the action that does the fix (a POA&M row: what closes it). */
  | { kind: 'fix'; row: GapRow; label: string; fix: GapFix }
  | { kind: 'audit'; label: string };

export interface GapsHero {
  tone: Tone | 'ok';
  /** G10: the big number, split from the sentence ("26 pts"). */
  big: string | null;
  /** G18: the big number's scale ("/110"). */
  unit: string | null;
  text: string;
  /** A second line (G22: the recoverable points under a blocked headline; G19: what the lever closes). */
  sub: string | null;
  /**
   * G14: the blocker on the headline line, linked to #gaps-must — "conditional blocked by
   * 4 Not met · 6 more unproven" (every must-Met not Met yet), "+1 blocker if 3.1.1 stands" only
   * when that proposal isn't already counted.
   */
  blocked: { count: number; text: string; more: string | null; extra: string | null } | null;
  primary: HeroPrimary | null;
  /** The rest, as named chips ("Then: 3.13.11 +5 · 3.1.20 +1"). */
  then: Array<{ kind: 'lever'; lever: Lever } | { kind: 'row'; row: GapRow; label: string }>;
}

export interface GapsModel {
  level: '1' | '2';
  hero: GapsHero;
  recoverablePts: number | null;
  scoreUpTo: number | null;
  mustMetOpen: number;
  /** Must-Mets with no reviewed result yet (no evidence or awaiting review). */
  mustUnproven: number;
  /** H12: the levers that close no-evidence gaps, biggest first. */
  levers: Lever[];
  counts: { needEvidence: number; waiting: number; notMet: number; stale: number };
  /** G15: while conditional, the date status ends (every POA&M item must close by it). */
  statusEnd: { date: string; days: number; tone: Tone } | null;
  noEvidence: GapRow[];
  waiting: GapRow[];
  notMetMust: GapRow[];
  notMetPoam: GapRow[];
  stale: GapRow[];
}

/** Which connector (if any) covers these sources: connected beats not connected. */
function connectorFor(sources: readonly string[], connectors: readonly ConnectorState[]): { connected: ConnectorState | null; kind: string | null; source: string | null } {
  let fallback: { kind: string; source: string } | null = null;
  for (const s of sources) {
    const kinds = SOURCE_KINDS[s];
    if (!kinds) continue;
    const hit = connectors.find((c) => kinds.includes(c.kind) && c.status === 'active');
    if (hit) return { connected: hit, kind: hit.kind, source: s };
    if (!fallback) {
      // The vendor the workspace already uses (an Okta AI-discovery connector → Okta posture).
      const vendor = kinds.find((k) => connectors.some((c) => KIND_VENDOR[c.kind] === KIND_VENDOR[k] || c.kind.split('_')[0] === KIND_VENDOR[k]));
      fallback = { kind: vendor ?? kinds[0]!, source: s };
    }
  }
  return { connected: null, kind: fallback?.kind ?? null, source: fallback?.source ?? null };
}

export const kindLabel = (kind: string) => KIND_LABEL[kind] ?? kind.replace(/_/g, ' ');

const uniq = <T,>(xs: readonly T[]) => [...new Set(xs)];
const clockTone = (n: number): Tone => (n < 10 ? 'red' : n < 30 ? 'amber' : 'neutral');
/** " · +5" — points only where there are points (Level 1 has none). */
export const plusPts = (n: number | null | undefined) => (n ? ` · +${n}` : '');

/** G2 / NEW-2: the hero names the item: "Re-check 3.5.3 · Okta · +5", "Update the SSP · 3.12.4". */
export function heroFixLabel(r: GapRow, o: { pts?: boolean } = {}): string {
  const f = r.fix;
  const p = o.pts === false ? '' : plusPts(r.points);
  if (f.kind === 'poam_open') return `Close ${r.ref}`;
  if (f.kind === 'recheck') return `Re-check ${r.ref} · ${f.source}${p}`;
  if (f.kind === 'collect') return `Collect now · ${r.ref} · ${f.source}${p}`;
  if (f.kind === 'connect') return `${f.label} · ${r.ref}${p}`;
  if (f.kind === 'poam') return `Add ${r.ref} to POA&M`;
  if (f.kind === 'ssp') return `Update the SSP · ${r.ref}`;
  if (f.kind === 'upload') return `${f.label.includes(r.ref) ? f.label : `${f.label} · ${r.ref}`}${p}`;
  return `${f.label} · ${r.ref}`;
}

/** Chip verbs (R3-3 / G21). */
export const VERB_SHORT: Record<GapFix['kind'], string> = {
  connect: 'Connect', collect: 'Collect', upload: 'Upload', request: 'Request', review: 'Review', poam: 'Add to POA&M', poam_open: 'Close', ssp: 'Update SSP', recheck: 'Re-check', nightly: 'Re-check',
};

export function buildGaps(input: AssessInput, drill: DrillModel = buildDrill(input)): GapsModel {
  const { score, tests = [], determinations = [], todayDay, connectors = [], findings = [] } = input;
  const level = drill.level;
  const l2 = level === '2' ? score?.level2 ?? null : null;
  const dets = new Map(determinations.map((d) => [d.determination_id, d] as const));
  const unmet = new Map((l2?.unmet ?? []).map((u) => [u.requirement_id, u] as const));
  const openItems = (input.poam ?? []).filter((p) => p.status === 'open');
  const poamBy = new Map(openItems.map((p) => [p.requirement_id, p] as const));
  // G15: conditional status ends when the earliest item's closeout falls due (the server's 180-day clock);
  // every row's deadline is that date, and nothing may close after it.
  const conditional = !!l2 && (l2.status === 'conditional' || l2.status === 'conditional_lapsed');
  // Items by closeout (stable, so equal dates keep the POA&M's order — the scorecard's "Close …" uses the same first item).
  const byDue = openItems.filter((p) => p.closeout_due_at).sort((a, b) => (a.closeout_due_at! < b.closeout_due_at! ? -1 : a.closeout_due_at! > b.closeout_due_at! ? 1 : 0));
  const dues = byDue.map((p) => p.closeout_due_at!);
  const itemRank = new Map(byDue.map((p, i) => [p.requirement_id, i] as const));
  const statusEnd = conditional && dues[0] ? { date: dues[0], days: daysUntil(dues[0], todayDay), tone: clockTone(daysUntil(dues[0], todayDay)) } : null;
  const openFindings = findings.filter((f) => f.status !== 'closed' && f.status !== 'auto_closed' && f.status !== 'resolved');
  const findingsFor = (refs: readonly string[]) => {
    const controls = new Set(tests.filter((t) => t.control_id && t.objectives.some((o) => refs.includes(reqRef(o.objective_id)))).map((t) => t.control_id!));
    return openFindings.filter((f) => f.control_id && controls.has(f.control_id)).map((f) => f.finding_id);
  };
  const base = (r: DrillRequirement): Omit<GapRow, 'missing' | 'fix'> => ({
    id: r.id, ref: r.ref, title: r.title, anchor: `gap-${dash(r.ref)}`, reqAnchor: r.anchor, points: r.points, mustMet: r.mustMet,
    uploadFor: [...r.objectives.filter((o) => o.status === 'no_evidence'), ...r.objectives.filter((o) => o.status !== 'no_evidence')].map((o) => ({ id: o.id, ref: o.ref })),
    findings: findingsFor(r.l2Refs), l2Refs: r.l2Refs,
  });

  const noEvidence: GapRow[] = [];
  const waiting: GapRow[] = [];
  const notMetMust: GapRow[] = [];
  const notMetPoam: GapRow[] = [];
  for (const r of drill.requirements) {
    if (r.status === 'no_evidence') {
      const empty = r.objectives.filter((x) => x.status === 'no_evidence');
      const sources = uniq(empty.flatMap((o) => o.strategy?.sources ?? []));
      const strat = empty[0]?.strategy ?? r.objectives[0]?.strategy ?? null;
      let fix: GapFix;
      const c = strat?.primary && AUTOMATED.has(strat.primary) ? connectorFor(sources, connectors) : null;
      if (c?.connected) fix = { kind: 'collect', label: 'Collect now', connectorId: c.connected.connector_id, source: kindLabel(c.connected.kind) };
      else if (c?.kind) fix = { kind: 'connect', label: `Connect ${kindLabel(c.kind)}`, connectorKind: c.kind, source: c.source! };
      else if (!strat || strat.primary === 'inherited_from_provider') fix = { kind: 'request', label: 'Request it' };
      else fix = { kind: 'upload', label: `Upload for ${r.ref}` };
      noEvidence.push({ ...base(r), missing: sources.length ? sources.join(' · ') : 'Evidence not named yet', fix });
    } else if (r.status === 'undetermined') {
      const p = r.objectives.flatMap((o) => o.cells).find((x) => x.proposal)?.proposal ?? null;
      const o = r.objectives.find((x) => x.status === 'undetermined');
      waiting.push({
        ...base(r), missing: p ? `${STATUS[p.result as CellStatus]?.label ?? p.result} proposed${p.reason ? ` · ${p.reason}` : ''}` : o?.reason ?? 'Waiting for a reviewed result',
        fix: p ? { kind: 'review', label: 'Review the result', determinationId: p.id } : { kind: 'request', label: 'Request a result' },
      });
    } else if (r.status === 'not_met') {
      const failing = r.objectives.flatMap((o) => o.cells.filter((c) => c.status === 'not_met').map((c) => ({ o, c })));
      const first = failing[0];
      const d = first?.c.determinationId ? dets.get(first.c.determinationId) : undefined;
      const u = unmet.get(r.id);
      const item = poamBy.get(r.id);
      const gapType = d?.gap_type ?? null;
      const sources = uniq(failing.flatMap(({ o }) => o.strategy?.sources ?? []));
      const strat = first?.o.strategy ?? null;
      // Re-check only with a connected source that reads the failing asset itself (an Okta cell → the Okta connector).
      const near = strat?.primary && AUTOMATED.has(strat.primary) ? connectorFor(sources, connectors) : null;
      const conn = near?.connected && failing.some(({ c }) => c.assetId && c.assetName.toLowerCase().includes(kindLabel(near.connected!.kind).toLowerCase())) ? near : null;
      // The fix by gap type: Update the SSP (not written down), Re-check (a connected source reads the asset), else new evidence.
      const byGap: GapFix = gapType === 'not_documented' ? { kind: 'ssp', label: 'Update the SSP' }
        : conn?.connected ? { kind: 'recheck', label: 'Re-check', connectorId: conn.connected.connector_id, source: kindLabel(conn.connected.kind) }
          : { kind: 'upload', label: `Upload new for ${r.ref}` };
      let fix: GapFix;
      let then: GapFix | null = null;
      if (item?.closeout_due_at) {
        // G15: the row's deadline is the status end; an item due later is flagged red.
        const end = statusEnd?.date ?? item.closeout_due_at;
        const n = daysUntil(end, todayDay);
        const late = item.closeout_due_at.slice(0, 10) > end.slice(0, 10);
        // G20: a row repeats the date only when it differs from the status end (the group title carries it).
        const same = !!statusEnd && item.closeout_due_at.slice(0, 10) === statusEnd.date.slice(0, 10);
        fix = late
          ? { kind: 'poam_open', label: `closeout ${fmtWithYear(item.closeout_due_at, todayDay)} · after status ends ${fmtWithYear(end, todayDay)}`, due: item.closeout_due_at, days: n, tone: 'red' }
          : same
            ? { kind: 'poam_open', label: '', due: end, days: n, tone: 'neutral' }
            : { kind: 'poam_open', label: n < 0 ? `closed by ${fmtWithYear(end, todayDay)} · passed` : `closes by ${fmtWithYear(end, todayDay)} · ${daysWord(n)}`, due: end, days: n, tone: statusEnd?.tone ?? clockTone(n) };
        // On the POA&M: the clock, then what closes it.
        then = byGap;
      } else if (u?.poam_id) {
        // On the POA&M but planned (no clock before conditional status): the item, then what closes it.
        fix = { kind: 'poam_open', label: 'On the POA&M · planned', due: '', days: 1e9, tone: 'neutral' };
        then = byGap;
      } else if (level === '2' && !r.mustMet && u && !u.poam_id && first?.c.determinationId) {
        fix = { kind: 'poam', label: 'Add to POA&M', determinationId: first.c.determinationId };
      } else {
        fix = byGap;
      }
      const row: GapRow = {
        ...base(r), missing: d?.rationale ?? first?.c.gap ?? 'Not met', fix, then,
        gap: first?.c.gap ?? null, why: d?.rationale ?? null, onPoam: !!item || !!u?.poam_id,
        chain: failing.map(({ o, c }) => ({ where: c.assetId ? c.assetName : 'Org-wide', tag: o.tag, name: o.statement || o.ref, cellKey: c.key })),
      };
      (level === '1' || r.mustMet ? notMetMust : notMetPoam).push(row);
    }
  }
  // F16: can't-go-on-POA&M first, then points lost.
  const byStake = (a: GapRow, b: GapRow) => Number(b.mustMet) - Number(a.mustMet) || (b.points ?? 0) - (a.points ?? 0) || a.ref.localeCompare(b.ref, 'en', { numeric: true });
  noEvidence.sort(byStake); waiting.sort(byStake); notMetMust.sort(byStake);
  notMetPoam.sort((a, b) => (itemRank.get(a.id) ?? 1e9) - (itemRank.get(b.id) ?? 1e9) || byStake(a, b));

  // H8 / H9: Met on stale evidence — counted, but blocks finishing; new evidence clears it.
  const stale: GapRow[] = [];
  const byL2 = new Map<string, DrillRequirement>();
  for (const r of drill.requirements) for (const ref of r.l2Refs) byL2.set(ref, r);
  for (const s of input.stale ?? []) {
    const r = byL2.get(reqRef(s.objective_id));
    if (!r || stale.some((x) => x.id === r.id)) continue;
    const line = staleLine(s.judged_at, s.window_days, todayDay);
    stale.push({
      ...base(r), missing: 'New evidence clears it', fix: { kind: 'upload', label: `Upload new for ${r.ref}` },
      uploadFor: [{ id: s.objective_id, ref: reqRef(s.objective_id) + (/\[[^\]]*\]$/.exec(s.objective_id)?.[0] ?? '') }],
      staleText: line.text, staleLead: line.lead, staleTail: line.tail, overdue: line.overdue,
    });
  }

  const collectable = l2 ? l2.not_yet_assessed.filter((id) => l2.closed_by_collection_alone.includes(id)) : [];
  const recoverablePts = l2 ? collectable.reduce((n, id) => n + (unmet.get(id)?.points ?? 0), 0) : null;
  const scoreUpTo = l2 && recoverablePts !== null ? Math.min(l2.max_score, l2.score + recoverablePts) : null;
  const mustMetOpen = notMetMust.length;
  const mustUnproven = level === '2' ? [...noEvidence, ...waiting].filter((r) => r.mustMet).length : 0;

  // G6 / N4: one lever per source group, sorted by points.
  const sumPts = (rows: GapRow[]) => rows.reduce((n, r) => n + (r.points ?? 0), 0);
  const refsWords = (rows: GapRow[]) => (rows.length <= 2 ? rows.map((r) => r.ref).join(', ') : reqs(rows.length));
  const levers: Lever[] = [];
  for (const kind of uniq(noEvidence.flatMap((r) => (r.fix.kind === 'connect' ? [r.fix.connectorKind] : [])))) {
    const rows = noEvidence.filter((r) => r.fix.kind === 'connect' && r.fix.connectorKind === kind);
    levers.push({ kind: 'connect', label: `Connect ${kindLabel(kind)} · ${refsWords(rows)}`, count: rows.length, pts: sumPts(rows), rows, first: rows[0]!, connectorKind: kind });
  }
  const collect = noEvidence.filter((r) => r.fix.kind === 'collect');
  if (collect.length) {
    const run = uniq(collect.map((r) => (r.fix.kind === 'collect' ? r.fix.connectorId : ''))).map((id) => {
      const r = collect.find((x) => x.fix.kind === 'collect' && x.fix.connectorId === id)!;
      return { connectorId: id, source: r.fix.kind === 'collect' ? r.fix.source : '' };
    });
    levers.push({ kind: 'collect', label: `Collect now · ${run.map((x) => x.source).join(', ')}`, count: collect.length, pts: sumPts(collect), rows: collect, first: collect[0]!, run });
  }
  for (const kind of ['upload', 'request'] as const) {
    const rows = noEvidence.filter((r) => r.fix.kind === kind);
    if (rows.length) levers.push({ kind, label: `${kind === 'upload' ? 'Upload evidence' : 'Request evidence'} · ${reqs(rows.length)}`, count: rows.length, pts: sumPts(rows), rows, first: rows[0]! });
  }
  levers.sort((a, b) => b.pts - a.pts || b.count - a.count);
  const leverThen = levers.slice(1).map((lever) => ({ kind: 'lever' as const, lever }));
  const VERB: Record<Lever['kind'], string> = { connect: 'connecting', collect: 'collecting', upload: 'uploading', request: 'requesting' };

  // ---- the hero, per state (G1–G5, G16–G24) ----
  let hero: GapsHero;
  const gate = input.gate ?? null;
  const signed = (input.affirmations ?? []).some((a) => a.period_id === score?.period_id && a.state === 'affirmed' && (gate?.allowed_kinds ?? []).includes(a.kind));
  // G24: the button says where it goes, not the headline again.
  const auditNext = gate?.finished && gate.allowed_kinds.length && !signed ? (gate.at_status ? 'Open Audit to affirm' : 'Open Audit to post') : null;
  /** The action that actually fixes a row (a POA&M row: what closes it, not its clock). */
  const doFix = (r: GapRow): GapFix => (r.fix.kind === 'poam_open' && r.then ? r.then : r.fix);
  const fixHero = (r: GapRow): HeroPrimary => {
    const fix = doFix(r);
    return { kind: 'fix', row: r, fix, label: heroFixLabel({ ...r, fix }, { pts: r.fix.kind !== 'poam_open' }) };
  };
  // G21 / R3-3: scroll-only chips carry a verb and a short name; on the clock states the deadline matters, not points.
  const rowChips = (rows: GapRow[], clock = false) => rows.map((row) => {
    const verb = VERB_SHORT[doFix(row).kind];
    // r5: verb + requirement id (never a truncated title).
    return { kind: 'row' as const, row, label: `${verb} · ${row.ref}${!clock && row.points ? ` +${row.points}` : ''}` };
  });
  if (level === '1' && score) {
    const l1 = score.level1;
    const lapse = level1Lapse(input.affirmations, todayDay);
    if (l1.status === 'met') {
      hero = { tone: 'ok', big: null, unit: null, text: `All ${l1.counts.requirements} met${auditNext && gate?.at_status ? ' · ready to affirm' : ''}`, sub: null, blocked: null, primary: auditNext ? { kind: 'audit', label: auditNext } : null, then: [] };
    } else {
      // NEW-9: the headline's verb is the CTA's verb. G19: under 30 days the clock leads.
      const lead = levers[0] ?? null;
      const closable = lead ? `${lead.count} closable by ${VERB[lead.kind]}` : null;
      const met = `${l1.counts.met} of ${l1.counts.requirements} met`;
      const clockFirst = !!lapse && lapse.days < 30;
      hero = {
        tone: 'red', big: null, unit: null,
        text: clockFirst
          ? (lapse!.days < 0 ? `Level 1 lapsed ${fmtWithYear(lapse!.date, todayDay)} · ${met}` : `Level 1 lapses ${fmtWithYear(lapse!.date, todayDay)} · ${daysWord(lapse!.days)} · ${met}`)
          : [`Not yet · ${met}`, closable, lapse ? lapse.text : null].filter(Boolean).join(' · '),
        sub: clockFirst ? closable : null,
        blocked: null,
        primary: lead ? { kind: 'lever', lever: lead } : notMetMust[0] ? fixHero(notMetMust[0]) : null,
        then: lead ? [...leverThen, ...rowChips(notMetMust)] : rowChips(notMetMust.slice(1)),
      };
    }
  } else if (l2) {
    const proposedMust = waiting.filter((r) => r.mustMet && r.fix.kind === 'review' && /^Not met/.test(r.missing));
    // G14: every must-Met not Met yet — judged Not met, plus the unproven (no evidence / awaiting review).
    const blocked = mustMetOpen || mustUnproven
      ? {
        count: mustMetOpen || mustUnproven, text: mustMetOpen ? 'Not met' : 'unproven', more: mustMetOpen && mustUnproven ? `${mustUnproven} unproven` : null,
        // A proposal already counted among the unproven isn't "+1" again.
        extra: proposedMust.length && !mustUnproven ? `+${proposedMust.length} blocker${proposedMust.length === 1 ? '' : 's'} if ${proposedMust.map((r) => r.ref).join(', ')} stand${proposedMust.length === 1 ? 's' : ''}` : null,
      }
      : null;
    if (conditional && statusEnd) {
      const n = statusEnd.days;
      const row = notMetPoam.find((r) => r.id === byDue[0]?.requirement_id) ?? notMetPoam[0] ?? null;
      hero = {
        tone: statusEnd.tone, big: null, unit: null,
        text: n < 0 ? `Conditional status lapsed ${fmtWithYear(statusEnd.date, todayDay)} · ${openItems.length} open`
          : `Conditional status ends ${fmtWithYear(statusEnd.date, todayDay)} · ${daysWord(n)} · ${openItems.length} open`,
        sub: null, blocked: null,
        // G16: the primary does the fix of the item that closes first ("Update the SSP · 3.8.9").
        primary: row ? fixHero(row) : null,
        then: rowChips(notMetPoam.filter((r) => r !== row), true),
      };
    } else if (l2.status === 'final' || (l2.status === 'conditional' && !openItems.length)) {
      hero = { tone: 'ok', big: null, unit: null, text: l2.status === 'final' ? 'Final: reached · nothing open' : 'Conditional: reached · nothing open', sub: null, blocked: null, primary: auditNext ? { kind: 'audit', label: auditNext } : null, then: [] };
    } else if (recoverablePts) {
      // G22: status leads (the blocker is the headline); the recoverable points are the second line.
      // G17: with a Not met must-Met, the primary is its fix; the point levers follow under "Then".
      const leadMust = notMetMust[0] ?? null;
      hero = {
        tone: blocked ? 'red' : 'neutral', big: blocked ? null : pts(recoverablePts), unit: null,
        text: blocked ? '' : `recoverable · up to ${scoreUpTo}`,
        sub: blocked ? `${pts(recoverablePts)} recoverable · up to ${scoreUpTo} · needs ${l2.conditional_threshold}` : null,
        blocked,
        primary: leadMust ? fixHero(leadMust) : levers[0] ? { kind: 'lever', lever: levers[0] } : null,
        then: leadMust ? levers.map((lever) => ({ kind: 'lever' as const, lever })) : leverThen,
      };
    } else {
      // Finished (or nothing collectable): the score on its scale, the 88 line, and what blocks conditional (G18).
      const lead = notMetMust[0] ?? notMetPoam[0] ?? null;
      const below = l2.score < l2.conditional_threshold;
      hero = {
        tone: mustMetOpen || below ? 'red' : 'neutral', big: String(l2.score), unit: `/${l2.max_score}`,
        text: [`needs ${l2.conditional_threshold}`, mustMetOpen ? `conditional blocked by ${plural(mustMetOpen, 'must-Met', 'must-Mets')}` : below ? `${l2.conditional_threshold - l2.score} pts short` : 'every requirement judged'].join(' · '),
        sub: null, blocked: null,
        primary: lead ? fixHero(lead) : auditNext ? { kind: 'audit', label: auditNext } : null,
        then: rowChips((notMetMust.length ? notMetMust : notMetPoam).filter((r) => r !== lead)),
      };
    }
  } else {
    hero = { tone: 'neutral', big: null, unit: null, text: noEvidence.length ? `${reqs(noEvidence.length)} closable by collecting evidence` : 'Nothing left to collect', sub: null, blocked: null, primary: levers[0] ? { kind: 'lever', lever: levers[0] } : null, then: leverThen };
  }

  return {
    level, hero, recoverablePts, scoreUpTo, mustMetOpen, mustUnproven, levers, statusEnd,
    counts: { needEvidence: noEvidence.length, waiting: waiting.length, notMet: notMetMust.length + notMetPoam.length, stale: stale.length },
    noEvidence, waiting, notMetMust, notMetPoam, stale,
  };
}

export { rowAnchor };
